import { useState } from 'react';
import { profile } from '../data/profile';
import { experiences } from '../data/experience';
import { skillCategories } from '../data/skills';
import { projects } from '../data/projects';
import { achievements } from '../data/achievements';
import { certificates } from '../data/certificates';
import { ui } from '../data/ui';
import { SEO } from '../components/ui/SEO';
import { SectionHeader } from '../components/ui/SectionHeader';
import { Button } from '../components/ui/Button';
import { ScrollReveal } from '../components/ui/ScrollReveal';
import { downloadResumePdf } from '../utils/downloadResumePdf';
import { whatsappContacts, buildTelUrl } from '../data/contact';
import './ResumePage.css';

export function ResumePage() {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownloadPdf = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    try {
      await downloadResumePdf();
    } catch (error) {
      console.error('Failed to generate resume PDF:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <SEO title={ui.seo.resume} description={profile.professionalSummary} />

      <div className="page-header">
        <div className="container">
          <SectionHeader
            label={ui.nav.resume}
            title={`${profile.name} — ${ui.resume.title}`}
            subtitle={`${profile.yearsOfExperience}+ ${ui.hero.yearsExperience} · ${ui.hero.since} ${profile.careerStartYear} · ${profile.title}`}
            align="center"
          />
          <div className="resume-download">
            <Button
              variant="primary"
              onClick={handleDownloadPdf}
              disabled={isGenerating}
            >
              {isGenerating ? ui.resume.generatingPdf : ui.resume.downloadPdf}
            </Button>
          </div>
        </div>
      </div>

      <section className="section resume-content">
        <div className="container">
          <div className="resume-grid">
            <ScrollReveal>
              <div className="resume-block glass-card">
                <h2>{ui.resume.summary}</h2>
                <p>{profile.professionalSummary}</p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="resume-block glass-card">
                <h2>{ui.resume.experience}</h2>
                {experiences.map((exp) => (
                  <div key={exp.id} className="resume-exp-item">
                    <div className="resume-exp-header">
                      <h3>{exp.role}</h3>
                      <span className="resume-exp-date">{exp.startDate} — {exp.endDate}</span>
                    </div>
                    <p className="resume-exp-company">{exp.company} · {exp.location}</p>
                    {exp.project && <p className="resume-exp-project">{ui.resume.project} {exp.project}</p>}
                    <ul>
                      {exp.highlights.slice(0, 3).map((h, i) => (
                        <li key={i}>{h}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="resume-block glass-card">
                <h2>{ui.resume.skills}</h2>
                {skillCategories.map((cat) => (
                  <div key={cat.id} className="resume-skill-group">
                    <h3>{cat.name}</h3>
                    <div className="tech-tags">
                      {cat.skills.filter((s) => !s.startsWith('[')).map((skill) => (
                        <span key={skill} className="tag tag-secondary">{skill}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="resume-block glass-card">
                <h2>{ui.resume.education}</h2>
                {profile.education.map((edu, i) => (
                  <div key={i} className="resume-edu-item">
                    <h3>{edu.degree} — {edu.field}</h3>
                    <p>{edu.institution}</p>
                    <span className="resume-exp-date">{edu.startYear} — {edu.endYear}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="resume-block glass-card">
                <h2>{ui.resume.projects}</h2>
                {projects.filter((p) => p.featured).map((project) => (
                  <div key={project.id} className="resume-project-item">
                    <h3>{project.name}</h3>
                    <p className="resume-exp-company">{project.role} · {project.type}</p>
                    <p>{project.shortDescription}</p>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="resume-block glass-card">
                <h2>{ui.resume.achievements}</h2>

                <h3 className="resume-subheading">{ui.certifications.achievementsTitle}</h3>
                {achievements.map((a) => (
                  <div key={a.id} className="resume-achievement-item">
                    <h3>{a.name}</h3>
                    <p>{a.description}</p>
                    {a.achievement && <span className="tag">{a.achievement}</span>}
                  </div>
                ))}

                <h3 className="resume-subheading">{ui.certifications.certificatesTitle}</h3>
                {certificates.map((cert) => (
                  <div key={cert.id} className="resume-cert-item">
                    <h3>{cert.name}</h3>
                    <p>{cert.issuer} · {cert.date}</p>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="resume-block glass-card">
                <h2>{ui.resume.contact}</h2>
                <div className="resume-contact">
                  <p><strong>{ui.resume.email}</strong> {profile.email}</p>
                  <p><strong>{ui.resume.phone}</strong></p>
                  {whatsappContacts.map((channel) => (
                    <p key={channel.id}>
                      <strong>
                        {channel.id === 'jordan' ? ui.resume.phoneJordan : ui.resume.phoneSaudi}:
                      </strong>{' '}
                      <a href={buildTelUrl(channel.number)}>{channel.display}</a>
                    </p>
                  ))}
                  <p><strong>{ui.resume.location}</strong> {profile.location}</p>
                  {profile.socialLinks.map((link) => (
                    <p key={link.name}>
                      <strong>{link.name}:</strong>{' '}
                      {link.url.startsWith('[') ? link.url : (
                        <a href={link.url} target="_blank" rel="noopener noreferrer">{link.url}</a>
                      )}
                    </p>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </>
  );
}
